import { useCallback, useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import { useTranslation } from '../i18n.js'
import {
  enrolTotp,
  loadTerminalToken,
  login,
  pinLogin,
  storeSession,
  type Session,
} from '../session/session.js'

/**
 * Signing in. `TEN-005`, `DIR-003`, `KB-02`.
 *
 * Small, and two things about it are not cosmetic.
 *
 * **A wrong password and an unreachable account read differently.** `DIR-003` is that an
 * unreachable directory "must never look like a wrong password", and this is the screen where that
 * either holds or does not. A shop told their password is wrong will change it, lock themselves
 * out, and phone somebody; a shop told the service is unreachable will wait.
 *
 * **`totp_required` is not a failure.** The credential was right and the second factor is being
 * asked for, so the screen adds a field rather than showing an error.
 */

export interface SignInProps {
  readonly baseUrl: string
  readonly onSignedIn: (session: Session) => void
  readonly doFetch: typeof globalThis.fetch
  /**
   * Over to signing up. Optional, because every test of this screen renders it alone and none of
   * them cares, and a required prop would mean editing all of them to add a callback nobody calls.
   */
  readonly onSignUp?: () => void
  /**
   * `TEN-006`: the enrolled terminal's token on this device. Absent reads it from storage; null
   * says there is none, which is how a test renders the password screen on a device that has one.
   */
  readonly terminalToken?: string | null
}

/** The keys of the PIN pad. Western digits whatever the page language (`LOC-005`). */
const PAD_DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const

export function SignIn({
  baseUrl,
  onSignedIn,
  doFetch,
  onSignUp,
  terminalToken,
}: SignInProps): JSX.Element {
  const { t } = useTranslation()
  /**
   * `TEN-006`. A device that has been enrolled as a terminal opens on the PIN pad, because a
   * shared till is where this screen is read most, and a person is one tap from the password form.
   */
  const [token] = useState<string | null>(() =>
    terminalToken === undefined ? loadTerminalToken() : terminalToken,
  )
  const [pinMode, setPinMode] = useState(token !== null)
  const [username, setUsername] = useState('')
  const [pin, setPin] = useState('')
  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [needsTotp, setNeedsTotp] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /**
   * The enrolment panel, and the secret once it has been handed over.
   *
   * On the sign-in screen because that is where somebody is standing when they discover they
   * cannot sign in: `TEN-005` makes TOTP mandatory for the owner and the manager, and until the
   * enrolment route existed neither could get in at all. Not offered automatically on a refusal,
   * because `login` deliberately cannot tell a missing authenticator from a wrong password, and
   * guessing between them would mean telling somebody with a mistyped password to go and set up an
   * authenticator they already have.
   */
  const [enrolling, setEnrolling] = useState(false)
  const [enrolled, setEnrolled] = useState<{
    readonly secret: string
    readonly uri: string
  } | null>(null)
  const first = useRef<HTMLInputElement>(null)
  const totpField = useRef<HTMLInputElement>(null)

  useEffect(() => {
    // KB-02: reachable without a mouse, starting with not needing one to begin.
    first.current?.focus()
  }, [])

  useEffect(() => {
    if (needsTotp) totpField.current?.focus()
  }, [needsTotp])

  const enrol = useCallback(async () => {
    if (identifier === '' || password === '') {
      setMessage(t('signIn.enrolNeedsBoth'))
      return
    }
    setBusy(true)
    setMessage(null)
    const result = await enrolTotp(baseUrl, { identifier, password }, doFetch)
    setBusy(false)

    switch (result.kind) {
      case 'enrolled':
        setEnrolled({ secret: result.secret, uri: result.uri })
        setMessage(t('signIn.enrolDone'))
        return
      case 'already_enrolled':
      case 'rejected':
      case 'unavailable':
        // The server's own words in all three cases. A shop that already has an authenticator is
        // told so, and a wrong password here says the same thing it says at the login.
        setMessage(result.message)
    }
  }, [baseUrl, doFetch, identifier, password, t])

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()
      setBusy(true)
      setMessage(null)
      const result = await login(
        baseUrl,
        { identifier, password, ...(totpCode === '' ? {} : { totpCode }) },
        doFetch,
      )
      setBusy(false)

      switch (result.kind) {
        case 'authenticated':
          storeSession(result.session)
          onSignedIn(result.session)
          return
        case 'totp_required':
          setNeedsTotp(true)
          setMessage(t('signIn.totpPrompt'))
          return
        case 'totp_invalid':
          // The field stays, and the message names the code rather than the password. Telling
          // somebody their password is wrong when it is not is how they lock themselves out of a
          // shop over a clock a minute fast.
          setNeedsTotp(true)
          setTotpCode('')
          setMessage(result.message)
          return
        case 'enrolment_required':
          /**
           * Open the enrolment panel rather than report a failure. `TEN-005`, `decisions/0014`.
           *
           * The password was accepted and the only missing piece is an authenticator, which this
           * screen can set up right here. Showing the refusal and leaving the panel behind the
           * "set up an authenticator" link would make the person read a message, hunt for a link,
           * and type the same two fields again.
           */
          setEnrolling(true)
          setMessage(result.message)
          return
        case 'unavailable':
          // A dead network is this client's own sentence, so it is said in the shop's language.
          // A 503 carries the server's wording and is shown as sent.
          setMessage(result.reason === 'network' ? t('signIn.unreachable') : result.message)
          return
        case 'rejected':
          setMessage(result.message)
      }
    },
    [baseUrl, doFetch, identifier, onSignedIn, password, t, totpCode],
  )

  const submitPin = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()
      if (token === null) return
      if (username.trim() === '' || pin === '') {
        setMessage(t('signIn.pin.needsBoth'))
        return
      }
      setBusy(true)
      setMessage(null)
      const result = await pinLogin(
        baseUrl,
        { terminalToken: token, username: username.trim(), pin },
        doFetch,
      )
      setBusy(false)

      switch (result.kind) {
        case 'authenticated':
          storeSession(result.session)
          onSignedIn(result.session)
          return
        case 'unavailable':
          setMessage(result.reason === 'network' ? t('signIn.pin.unreachable') : result.message)
          return
        case 'totp_required':
        case 'totp_invalid':
        case 'rejected':
          // A refused PIN is cleared, so the next person at a shared till does not inherit the
          // digits, and the sentence is the server's, which names none of the halves.
          setPin('')
          setMessage(result.kind === 'totp_required' ? t('signIn.pin.needsBoth') : result.message)
      }
    },
    [baseUrl, doFetch, onSignedIn, pin, t, token, username],
  )

  if (pinMode && token !== null) {
    return (
      <section className="lm-screen lm-screen--narrow" aria-labelledby="sign-in-heading">
        <h1 id="sign-in-heading" className="lm-screen__heading">
          {t('signIn.pin.heading')}
        </h1>

        <form
          onSubmit={(e) => {
            void submitPin(e)
          }}
          className="lm-form"
        >
          <label className="lm-field">
            <span className="lm-field__label">{t('signIn.pin.username')}</span>
            <input
              ref={first}
              className="lm-input"
              // A short name is Latin whatever the page direction, and must not mirror.
              dir="ltr"
              autoCapitalize="none"
              autoComplete="off"
              spellCheck={false}
              value={username}
              onChange={(e) => setUsername(e.currentTarget.value)}
            />
          </label>

          <label className="lm-field">
            <span className="lm-field__label">{t('signIn.pin.pin')}</span>
            {/* A real field, so a keyboard works (KB-02) and so does a password manager. The pad
                below types into the same state. Masked, because a till faces the customer. */}
            <input
              className="lm-input"
              dir="ltr"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              value={pin}
              onChange={(e) => setPin(e.currentTarget.value)}
            />
          </label>

          {/* `dir="ltr"` on the grid: RTL mirrors layout but never the digits, and a pad whose
              seven and nine had traded places would be a pad nobody could use from memory. */}
          <div className="lm-keypad" dir="ltr">
            {PAD_DIGITS.map((digit) => (
              <button
                key={digit}
                type="button"
                className="lm-button lm-keypad__key"
                onClick={() => setPin((was) => was + digit)}
              >
                {digit}
              </button>
            ))}
            <button
              type="button"
              className="lm-button lm-keypad__key"
              onClick={() => setPin((was) => was.slice(0, -1))}
            >
              {t('signIn.pin.backspace')}
            </button>
            <button
              type="button"
              className="lm-button lm-keypad__key"
              onClick={() => setPin((was) => was + '0')}
            >
              0
            </button>
            <button type="button" className="lm-button lm-keypad__key" onClick={() => setPin('')}>
              {t('signIn.pin.clear')}
            </button>
          </div>

          <button type="submit" className="lm-button lm-button--primary" disabled={busy}>
            {busy ? t('signIn.busy') : t('signIn.submit')}
          </button>
        </form>

        <button
          type="button"
          className="lm-button"
          onClick={() => {
            setPinMode(false)
            setMessage(null)
            setPin('')
          }}
        >
          {t('signIn.pin.usePassword')}
        </button>

        {message ? (
          <p className="lm-hint" role="status">
            {message}
          </p>
        ) : null}
      </section>
    )
  }

  return (
    <section className="lm-screen lm-screen--narrow" aria-labelledby="sign-in-heading">
      <h1 id="sign-in-heading" className="lm-screen__heading">
        {t('signIn.heading')}
      </h1>

      <form
        onSubmit={(e) => {
          void submit(e)
        }}
        className="lm-form"
      >
        <label className="lm-field">
          <span className="lm-field__label">{t('signIn.email')}</span>
          <input
            ref={first}
            className="lm-input"
            // `LOC-005`: an address is Latin whatever the page direction, and it must not mirror.
            dir="ltr"
            type="email"
            autoComplete="username"
            value={identifier}
            onChange={(e) => setIdentifier(e.currentTarget.value)}
          />
        </label>

        <label className="lm-field">
          <span className="lm-field__label">{t('signIn.password')}</span>
          <input
            className="lm-input"
            dir="ltr"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
          />
        </label>

        {needsTotp ? (
          <label className="lm-field">
            <span className="lm-field__label">{t('signIn.totp')}</span>
            <input
              ref={totpField}
              className="lm-input"
              dir="ltr"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={totpCode}
              onChange={(e) => setTotpCode(e.currentTarget.value)}
            />
          </label>
        ) : null}

        <button type="submit" className="lm-button lm-button--primary" disabled={busy}>
          {busy ? t('signIn.busy') : t('signIn.submit')}
        </button>
      </form>

      {enrolled === null ? (
        enrolling ? (
          <section className="lm-enrol" aria-labelledby="enrol-heading">
            <h2 id="enrol-heading" className="lm-enrol__heading">
              {t('signIn.enrolHeading')}
            </h2>
            <p className="lm-enrol__means">{t('signIn.enrolMeans')}</p>
            <button
              type="button"
              className="lm-button lm-button--secondary"
              disabled={busy}
              onClick={() => {
                void enrol()
              }}
            >
              {t('signIn.enrol')}
            </button>
            <button
              type="button"
              className="lm-button"
              onClick={() => {
                setEnrolling(false)
                setMessage(null)
              }}
            >
              {t('signIn.enrolBack')}
            </button>
          </section>
        ) : (
          <button
            type="button"
            className="lm-button"
            onClick={() => {
              setEnrolling(true)
              setMessage(null)
            }}
          >
            {t('signIn.enrolOpen')}
          </button>
        )
      ) : (
        <section className="lm-enrol" aria-labelledby="enrol-heading">
          <h2 id="enrol-heading" className="lm-enrol__heading">
            {t('signIn.enrolHeading')}
          </h2>
          {/* The secret, in the data face and selectable, because somebody is copying it into
              another app by hand. `dir="ltr"`: base32 is Latin whatever the page direction. */}
          <span className="lm-field__label">{t('signIn.enrolSecret')}</span>
          <code className="lm-enrol__secret" dir="ltr">
            {enrolled.secret}
          </code>
          {/* The same secret as a link an authenticator app can open, which is what a phone does
              with a QR code. No QR here: it would need an image library on the sign-in screen. */}
          <a className="lm-enrol__link" href={enrolled.uri}>
            {t('signIn.enrolLink')}
          </a>
          <button
            type="button"
            className="lm-button"
            onClick={() => {
              setEnrolled(null)
              setEnrolling(false)
              setMessage(null)
            }}
          >
            {t('signIn.enrolBack')}
          </button>
        </section>
      )}

      {token === null ? null : (
        <button
          type="button"
          className="lm-button"
          onClick={() => {
            setPinMode(true)
            setMessage(null)
          }}
        >
          {t('signIn.pin.usePin')}
        </button>
      )}

      {onSignUp === undefined ? null : (
        // `OPS-201`. Underneath, because the returning majority are here to sign in.
        <button type="button" className="lm-button" onClick={onSignUp}>
          {t('signIn.signUp')}
        </button>
      )}

      {message ? (
        <p className="lm-hint" role="status">
          {message}
        </p>
      ) : null}
    </section>
  )
}
